import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import { motion, useScroll, useTransform, useSpring, useInView, animate } from 'framer-motion';
import {
    FaCloud, FaCode, FaMobileAlt, FaDraftingCompass,
    FaShieldAlt
} from 'react-icons/fa';
import { BsCheckCircleFill } from 'react-icons/bs';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

const Counter = ({ value, suffix = "" }) => {
    const [count, setCount] = useState(0);
    const ref = React.useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    useEffect(() => {
        if (isInView) {
            const controls = animate(0, value, {
                duration: 2.5,
                ease: "easeOut",
                onUpdate: (latest) => {
                    setCount(Math.floor(latest));
                }
            });
            return () => controls.stop();
        }
    }, [isInView, value]);

    return (
        <span ref={ref} className="stat-number">
            {count}{suffix}
        </span>
    );
};

const Home = () => {
    const navigate = useNavigate();
    const { scrollYProgress, scrollY } = useScroll();
    const [services, setServices] = useState([]);
    const [experienceStats, setExperienceStats] = useState([]);

    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const { ok, data: result } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/services?t=${Date.now()}`, {
                    headers: { 'Accept': 'application/json' }
                });
                if (ok) {
                    const servicesData = result.data || result;
                    const finalData = Array.isArray(servicesData) ? servicesData : [];
                    console.log('Fetched services count:', finalData.length);
                    setServices(finalData);
                }
            } catch (error) {
                console.error('Error fetching services:', error);
            }
        };

        const fetchExperienceStats = async () => {
            try {
                const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/experience-stats', {
                    headers: { 'Accept': 'application/json' }
                });
                if (ok) {
                    const data = result.data || result;
                    setExperienceStats(Array.isArray(data) ? data : Object.values(data));
                }
            } catch (error) {
                console.error('Error fetching experience stats:', error);
            }
        };

        fetchServices();
        fetchExperienceStats();
    }, []);

    const getIcon = (iconName) => {
        switch (iconName?.toLowerCase()) {
            case 'cloud': return FaCloud;
            case 'code': return FaCode;
            case 'mobile': return FaMobileAlt;
            case 'design': return FaDraftingCompass;
            default: return FaCode;
        }
    };

    const parseStatValue = (val) => {
        if (!val) return { numericValue: 0, suffix: "" };
        if (typeof val === 'number') return { numericValue: val, suffix: "" };
        const valStr = String(val);
        const match = valStr.match(/^(\d+)(.*)$/);
        if (match) {
            return { numericValue: parseInt(match[1]), suffix: match[2] };
        }
        return { numericValue: 0, text: valStr };
    };

    // Parallax transforms
    const heroTextY = useTransform(scrollY, [0, 500], [0, 150]);
    const heroImageY = useTransform(scrollY, [0, 500], [0, -100]);
    const statsY = useTransform(scrollY, [0, 800], [0, -50]);

    const displayServices = services.length > 0 ? services : [
        { id: 'def1', title: "Cloud Platforms", description: "AWS, Azure, and GCP infrastructure optimization for seamless scalability and security.", icon: "cloud" },
        { id: 'def2', title: "Web Development", description: "High-performance React and Node.js applications built for modern enterprise demands.", icon: "code" },
        { id: 'def3', title: "Mobile Apps", description: "Native and cross-platform mobile experiences that engage users on any device.", icon: "mobile" },
        { id: 'def4', title: "Ethics & UI/UX", description: "Purpose-driven, accessible designs that balance aesthetics with human-centric ethics.", icon: "design" }
    ];

    const defaultStats = [
        { label: 'Years Experience', value: "10+" },
        { label: 'Projects Delivered', value: "200+" },
        { label: 'Global Clients', value: "50+" },
        { label: 'Tech Support', value: '24/7' }
    ];

    const displayStats = experienceStats.length > 0 ? experienceStats : defaultStats;

    return (
        <div className="home-container">
            {/* Scroll Progress Bar */}
            <motion.div
                style={{
                    scaleX,
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "6px",
                    background: "linear-gradient(to right, #FF9F1C, #FF4B1F)",
                    transformOrigin: "0%",
                    zIndex: 9999
                }}
            />

            <Navbar />

            {/* Hero Section */}
            <header className="hero-section" style={{ marginTop: '0px', paddingTop: '150px' }}>
                <motion.div
                    className="hero-bg-circle"
                    animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.8, 0.5] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="hero-content"
                    style={{ y: heroTextY }}
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                >
                    <motion.h1 className="hero-title" variants={fadeInUp}>
                        Web and Mobile App <br />
                        <span className="hero-highlight">Development Company in Vadodara</span>
                    </motion.h1>
                    <motion.p className="hero-desc" variants={fadeInUp}>
                        We build scalable, high-performance web and mobile solutions tailored to your business goals. Premium digital agency specializing in Cloud, Web, and UI/UX.
                    </motion.p>
                    <motion.div className="hero-buttons" variants={fadeInUp}>
                        <motion.button
                            className="btn-primary"
                            style={{ backgroundColor: '#FF9F1C' }}
                            whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(255, 159, 28, 0.3)" }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/contact')}
                        >
                            Get a Free Consultation
                        </motion.button>
                        <motion.button
                            className="btn-outline"
                            whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/portfolio')}
                        >
                            View Portfolio
                        </motion.button>
                    </motion.div>
                </motion.div>

                <motion.div
                    className="hero-visual"
                    style={{ y: heroImageY }}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, delay: 0.5 }}
                >
                    <motion.div
                        className="visual-card"
                        animate={{ y: [-10, 10, -10] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <img
                            src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                            alt="Web and Mobile App Development Company in Vadodara"
                            className="visual-image"
                            loading="lazy"
                        />
                        <motion.div
                            className="visual-badge"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 1.5, duration: 0.5 }}
                        >
                            <FaShieldAlt className="badge-icon" />
                            <div className="badge-text">
                                <span className="badge-label">QUALITY ASSURANCE</span>
                                <span className="badge-value">99.9% Up-time</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </header>

            {/* Stats Bar */}
            <motion.div
                className="stats-bar"
                style={{ y: statsY }}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
            >
                {displayStats.map((stat, i) => {
                    const { numericValue, suffix, text } = parseStatValue(stat.value);
                    const uniqueKey = stat.id ? `stat-${stat.id}` : `stat-def-${i}`;
                    return (
                        <div className="stat-item" key={uniqueKey}>
                            {text ? (
                                <span className="stat-number">{text}</span>
                            ) : (
                                <Counter value={numericValue} suffix={suffix} />
                            )}
                            <span className="stat-label">{stat.label}</span>
                        </div>
                    );
                })}
            </motion.div>

            {/* Services Section */}
            <section className="services-section" id="services">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="section-eyebrow">WHAT WE DO</span>
                    <h2 className="section-title">Our Core Services {services.length > 0 && `(${services.length})`}</h2>
                </motion.div>

                <div className="services-grid" style={{ marginTop: '50px' }}>
                    {displayServices.map((service, index) => {
                        const Icon = getIcon(service.icon);
                        return (
                            <motion.div
                                className="service-card"
                                key={`svc-${service.id || index}`}
                                variants={fadeInUp}
                                initial="visible"
                                whileInView="visible"
                                viewport={{ once: true }}
                                whileHover={{
                                    y: -15,
                                    backgroundColor: "#fff",
                                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div className="service-icon-box">
                                    <Icon />
                                </div>
                                <h3 className="service-title">{service.title}</h3>
                                <p className="service-desc">{service.description}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* Philosophy Section */}
            <section className="philosophy-section">
                <motion.div
                    className="philosophy-content"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="section-eyebrow" style={{ color: '#FF9F1C' }}>OUR PHILOSOPHY</span>
                    <h2>Why Choose Nimai Solutions</h2>
                    <p className="philosophy-desc">
                        We don’t just build software; we build foundations for growth. Our approach blends technical excellence with a conscious, spiritual-subtle aesthetic that focuses on value creation and long-term sustainability.
                    </p>

                    <div className="philosophy-list">
                        {[
                            "Integrity & Transparency",
                            "Scalable Engineering",
                            "Human-Centric Design"
                        ].map((item, index) => (
                            <motion.div
                                className="list-item"
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 + index * 0.1 }}
                                whileHover={{ x: 10, transition: { duration: 0.2 } }}
                            >
                                <BsCheckCircleFill className="check-icon" />
                                <div>
                                    <span className="item-title">{item}</span>
                                    <p className="item-desc">Focused on delivering real value and impactful results.</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                <div className="philosophy-images">
                    <motion.img
                        src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                        className="img-1"
                        alt="Mobile App Development Team in Vadodara"
                        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -50]) }}
                        loading="lazy"
                    />
                    <motion.img
                        src="https://images.unsplash.com/photo-1531403009284-440f080d1e12?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                        className="img-2"
                        alt="Flutter App Development Services"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        loading="lazy"
                    />
                </div>
            </section>

            {/* CTA Section */}
            <div className="cta-wrapper">
                <motion.div
                    className="cta-card"
                    initial={{ opacity: 0, scale: 0.9, rotateX: 10 }}
                    whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
                >
                    <h2 className="cta-title">Ready to start your next project?</h2>
                    <p className="cta-text">
                        Join 50+ successful businesses that transformed their digital presence with Nimai Solutions. Let's discuss your vision today.
                    </p>
                    <div className="cta-buttons">
                        <motion.button
                            className="btn-primary"
                            style={{ backgroundColor: '#FF9F1C' }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/contact')}
                        >
                            Talk to Our Experts
                        </motion.button>
                        <motion.button
                            className="btn-dark"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/portfolio')}
                        >
                            Browse Case Studies
                        </motion.button>
                    </div>
                </motion.div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Home;
